import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./PendingActions.css";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import { toLocalDateStr, formatDateDMY } from "../utils/date";
import { getUserRole } from "../utils/auth";
import ToastContainer, { useToast } from "./common/Toast";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Display-only relabeling of BookingSource values - mirrors
// UserInventory.jsx's own sourceLabel exactly, so a booking's source reads
// the same wherever it's shown.
const sourceLabel = (source) =>
  ({ CALL: "VINTARA", CALLS_GST: "VINTARA + GST BILL" }[source]) || source;

// The two endpoints below are already resort-scoped server-side to the
// caller's own assignments (ResortAccessService) - "All Resorts vs one
// resort" is just a client-side filter on the already-fetched lists, not a
// third query param on two endpoints.
const PendingActions = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const [resorts, setResorts] = useState([]);
  const [selectedResort, setSelectedResort] = useState(null);

  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [pendingCheckIns, setPendingCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check-In, handled right here with the exact same modal/flow
  // UserInventory.jsx's own Check-In button opens - check-in and balance
  // collection are a single bundled action (BookingService.checkInPayment),
  // so there's no separate "collect balance" step here.
  const [checkInRow, setCheckInRow] = useState(null);
  const [splitRows, setSplitRows] = useState([{ key: 0, paymentAccount: null, amount: "" }]);
  const [creditDestinations, setCreditDestinations] = useState([]);
  const [isSubmittingCheckIn, setIsSubmittingCheckIn] = useState(false);
  const [checkInDiscount, setCheckInDiscount] = useState("");
  const [checkInDiscountReason, setCheckInDiscountReason] = useState("");

  useEffect(() => {
    const fetchResorts = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/api/resorts/resort-names-drop-down`, {
          headers: config.getHeaders(),
        });
        const data = await res.json();
        const options = data.map((r) => ({ value: r.id, label: r.name }));
        setResorts([{ value: null, label: "All Resorts" }, ...options]);
      } catch (err) {
        console.error("Failed to load resorts:", err);
        setResorts([]);
      }
    };
    fetchResorts();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const headers = config.getHeaders();
        const [documents, checkIns] = await Promise.all([
          fetch(`${config.BASE_URL}/api/front-desk/pending-documents`, { headers }).then((r) => r.json()),
          fetch(`${config.BASE_URL}/api/front-desk/pending-checkins`, { headers }).then((r) => r.json()),
        ]);
        setPendingDocuments(documents || []);
        setPendingCheckIns(checkIns || []);
      } catch (err) {
        console.error("Failed to load front desk queue:", err);
        setPendingDocuments([]);
        setPendingCheckIns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const matchesResort = (row) => !selectedResort?.value || row.resortId === selectedResort.value;

  const filteredDocuments = pendingDocuments.filter(matchesResort);
  const filteredCheckIns = pendingCheckIns.filter(matchesResort);

  const bookingLink = (row) =>
    `/user/inventory?resortId=${row.resortId}&tab=BOOKINGS&bookingFilterId=${row.bookingId}`;

  // Check-in is allowed any day from the booking's arrival date (checkInDate)
  // through its departure date (checkOutDate) - mirrors
  // UserInventory.jsx's canCheckInToday/canOverrideLateCheckIn/canCheckInNow
  // exactly, so a booking behaves identically whether checked in from here
  // or from Inventory.
  const todayStr = useMemo(() => toLocalDateStr(new Date()), []);
  const canCheckInToday = (row) => todayStr >= row.checkInDate && todayStr <= row.checkOutDate;
  const canOverrideLateCheckIn = ["SUPER_USER", "USER"].includes(getUserRole());
  const canCheckInNow = (row) =>
    canCheckInToday(row) || (canOverrideLateCheckIn && todayStr > row.checkOutDate);

  const addSplitRow = () => {
    setSplitRows((prev) => [...prev, { key: (prev[prev.length - 1]?.key ?? 0) + 1, paymentAccount: null, amount: "" }]);
  };

  const removeSplitRow = (key) => {
    setSplitRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
  };

  const updateSplitRow = (key, changes) => {
    setSplitRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...changes } : r)));
  };

  const splitTotal = splitRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const openCheckInModal = async (row) => {
    setCheckInRow(row);
    const dueAtCheckIn = row.pendingBalanceAmount ?? row.balanceAmount ?? 0;
    setSplitRows([{ key: 0, paymentAccount: null, amount: dueAtCheckIn > 0 ? String(dueAtCheckIn) : "" }]);
    setCheckInDiscount("");
    setCheckInDiscountReason("");
    setCreditDestinations([]);
    try {
      const res = await fetch(`${config.BASE_URL}/api/resorts/${row.resortId}/payment-accounts`, {
        headers: config.getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch payment accounts");
      const data = await res.json();
      setCreditDestinations(data.map((a) => ({ value: a.id, label: a.name })));
    } catch (err) {
      console.error(err);
      setCreditDestinations([]);
    }
  };

  const closeCheckInModal = () => {
    setCheckInRow(null);
  };

  // Splits must sum to this, not the raw balance, once a discount is entered.
  const effectiveBalanceDue = Math.max(
    0,
    (checkInRow?.pendingBalanceAmount ?? checkInRow?.balanceAmount ?? 0) - (parseFloat(checkInDiscount) || 0)
  );

  const handleDiscountChange = (value) => {
    setCheckInDiscount(value);
    // Convenience: with a single split row, keep it in sync with the new
    // effective balance automatically - with more than one row it's
    // ambiguous how to redistribute, so leave those to staff.
    if (splitRows.length === 1) {
      const newEffective = Math.max(0, (checkInRow?.pendingBalanceAmount ?? checkInRow?.balanceAmount ?? 0) - (parseFloat(value) || 0));
      setSplitRows([{ ...splitRows[0], amount: newEffective > 0 ? String(newEffective) : "" }]);
    }
  };

  const handleCheckInSubmit = async () => {
    if (!checkInRow || isSubmittingCheckIn) return;

    const discountValue = parseFloat(checkInDiscount) || 0;
    const balanceDueForDiscount = checkInRow.pendingBalanceAmount ?? checkInRow.balanceAmount ?? 0;
    if (discountValue < 0 || discountValue > balanceDueForDiscount) {
      showToast(`Discount must be between ₹0 and the balance due (₹${balanceDueForDiscount}).`, "warning");
      return;
    }
    if (discountValue > 0 && !checkInDiscountReason.trim()) {
      showToast("Please enter a reason for the discount.", "warning");
      return;
    }

    let splitsPayload = [];
    if (effectiveBalanceDue > 0) {
      if (splitRows.some((r) => !r.paymentAccount || !r.amount || parseFloat(r.amount) <= 0)) {
        showToast("Please select an account and enter an amount for every split row.", "warning");
        return;
      }
      if (Math.round(splitTotal * 100) !== Math.round(effectiveBalanceDue * 100)) {
        showToast(`Split amounts (₹${splitTotal}) must add up to the balance due (₹${effectiveBalanceDue}).`, "warning");
        return;
      }
      splitsPayload = splitRows.map((r) => ({ paymentAccountId: r.paymentAccount.value, amount: parseFloat(r.amount) }));
    }

    setIsSubmittingCheckIn(true);
    try {
      const params = new URLSearchParams();
      if (discountValue > 0) {
        params.set("discountAmount", discountValue);
        params.set("discountReason", checkInDiscountReason.trim());
      }
      const url = `${config.BASE_URL}/api/bookings/${checkInRow.bookingId}/check-in${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: config.getHeaders(),
        body: JSON.stringify(splitsPayload),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Check-in failed");
      }

      // Resolved - drop it from the list rather than re-fetching both
      // queries just for one row.
      setPendingCheckIns((prev) => prev.filter((r) => r.bookingId !== checkInRow.bookingId));
      showToast("Check-in complete.", "success");
      setCheckInRow(null);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Check-in failed. Please try again.", "danger");
    } finally {
      setIsSubmittingCheckIn(false);
    }
  };

  return (
    <div className="pending-actions">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="vt-page-header">
        <h2>Pending Actions</h2>
      </div>

      <div className="pending-actions-filters">
        <label>Resort</label>
        <Select
          options={resorts}
          value={selectedResort}
          onChange={setSelectedResort}
          placeholder="All Resorts"
          isClearable
          isSearchable={false}
          classNamePrefix="react-select"
          className="react-select-container filter-select"
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="attendance-roster-card">
        <div className="attendance-roster-header">
          <h2 className="section-title">Pending Check-Ins ({filteredCheckIns.length})</h2>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Resort</th>
                <th>Source</th>
                <th>Check-In Date</th>
                <th>Rooms</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCheckIns.length > 0 ? (
                filteredCheckIns.map((row) => (
                  <tr key={row.bookingId}>
                    <td>{row.customerName}</td>
                    <td>{row.resortName}</td>
                    <td>{sourceLabel(row.source)}</td>
                    <td>{formatDate(row.checkInDate)}</td>
                    <td>{(row.roomNumbers || []).join(", ") || "-"}</td>
                    <td>
                      {row.daysOverdue > 0 ? (
                        <span className="vt-badge vt-badge-danger">
                          {row.daysOverdue} day{row.daysOverdue === 1 ? "" : "s"} overdue
                        </span>
                      ) : (
                        <span className="vt-badge vt-badge-warning">Due today</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="vt-btn vt-btn-primary"
                        onClick={() => openCheckInModal(row)}
                        disabled={!canCheckInNow(row)}
                        title={
                          canCheckInToday(row)
                            ? undefined
                            : todayStr < row.checkInDate
                            ? `Check-in opens on ${formatDateDMY(row.checkInDate)}`
                            : canOverrideLateCheckIn
                            ? `Window closed after ${formatDateDMY(row.checkOutDate)} - late check-in override`
                            : `Check-in window closed after ${formatDateDMY(row.checkOutDate)}`
                        }
                      >
                        Check In
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>Nothing pending</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="attendance-roster-card">
        <div className="attendance-roster-header">
          <h2 className="section-title">Document Verification ({filteredDocuments.length})</h2>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Guest</th>
                <th>Resort</th>
                <th>Source</th>
                <th>Check-In</th>
                <th>Uploaded</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((row) => (
                  <tr key={row.guestDocumentId}>
                    <td>{row.guestName}</td>
                    <td>{row.resortName}</td>
                    <td>{sourceLabel(row.source)}</td>
                    <td>{formatDate(row.checkInDate)}</td>
                    <td>{formatDate(row.uploadedAt)}</td>
                    <td>
                      <Link className="vt-btn vt-btn-primary" to={bookingLink(row)}>
                        Review Documents
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>Nothing pending</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {checkInRow && (
        <div className="modal-overlay">
          <div className="modal checkin-modal">
            <h3>Complete Check-In &amp; Collect Balance</h3>

            <div className="checkin-modal__summary">
              <p><strong>Lead Guest:</strong> {checkInRow.customerName}</p>
              <p><strong>Total Bill:</strong> ₹{checkInRow.totalAmount}</p>
              <p><strong>Outstanding Balance Due:</strong> <span className="checkin-modal__balance">₹{checkInRow.pendingBalanceAmount ?? checkInRow.balanceAmount}</span></p>
            </div>

            {(checkInRow.pendingBalanceAmount ?? checkInRow.balanceAmount) > 0
              && (checkInRow.source === "CALL" || checkInRow.source === "CALLS_GST") && (
              <div className="checkin-modal__section">
                <label className="field-label">Discount (optional) - Vintara bookings only</label>
                <input
                  type="number"
                  min="0"
                  max={checkInRow.pendingBalanceAmount ?? checkInRow.balanceAmount}
                  value={checkInDiscount}
                  onChange={(e) => handleDiscountChange(e.target.value)}
                  placeholder="0"
                  className="checkin-modal__input"
                />
                {parseFloat(checkInDiscount) > 0 && (
                  <>
                    <input
                      type="text"
                      value={checkInDiscountReason}
                      onChange={(e) => setCheckInDiscountReason(e.target.value)}
                      placeholder="Reason for discount (required)"
                      className="checkin-modal__input"
                      style={{ marginTop: "8px" }}
                    />
                    <p className="checkin-modal__hint">
                      Amount to collect after discount: <strong>₹{effectiveBalanceDue}</strong>
                    </p>
                  </>
                )}
              </div>
            )}

            {effectiveBalanceDue > 0 && (
              <div className="checkin-modal__section">
                <label className="field-label">
                  Credit Account Destination{splitRows.length > 1 ? "s (split payment)" : ""}
                </label>
                {splitRows.map((row) => (
                  <div key={row.key} className="split-row">
                    <div className="split-row__account">
                      <Select
                        options={creditDestinations}
                        value={row.paymentAccount}
                        onChange={(opt) => updateSplitRow(row.key, { paymentAccount: opt })}
                        placeholder="Select account..."
                        classNamePrefix="react-select"
                        menuPortalTarget={menuPortalTarget}
                        menuPosition={menuPosition}
                        styles={themedSelectStyles()}
                      />
                    </div>
                    <input
                      type="number"
                      value={row.amount}
                      onChange={(e) => updateSplitRow(row.key, { amount: e.target.value })}
                      placeholder="Amount"
                      className="checkin-modal__input split-row__amount"
                    />
                    <button
                      type="button"
                      onClick={() => removeSplitRow(row.key)}
                      title="Remove this split"
                      className="split-row__remove"
                      disabled={splitRows.length === 1}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                <button type="button" onClick={addSplitRow} className="add-split-btn">
                  + Add Split
                </button>

                <p className={`allocation-status ${splitTotal === effectiveBalanceDue ? "is-balanced" : "is-mismatched"}`}>
                  Allocated: ₹{splitTotal} / ₹{effectiveBalanceDue}
                </p>
              </div>
            )}

            <div className="checkin-modal__actions">
              <button
                className="checkin-btn"
                onClick={handleCheckInSubmit}
                disabled={
                  isSubmittingCheckIn ||
                  (effectiveBalanceDue > 0 && splitTotal !== effectiveBalanceDue) ||
                  (parseFloat(checkInDiscount) > 0 && !checkInDiscountReason.trim())
                }
              >
                {isSubmittingCheckIn ? "Checking In..." : "Confirm Check-In"}
              </button>
              <button className="cancel-btn" onClick={closeCheckInModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingActions;
